'use client';

import React, { useState, useEffect } from 'react';
import { Layout, Menu, Select, Space, Typography, Avatar, Drawer, Button } from 'antd';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  UserOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SwapOutlined,
  MenuOutlined,
  CloseOutlined,
  HomeOutlined,
  FileTextOutlined,
  ShoppingOutlined,
  InboxOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { usePathname, useRouter } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from './AuthContext';
import { NAVIGATION_ITEMS, getAuthorizedNavItems, NavItemConfig } from '@/lib/navigation';
import { RoleName } from '@/lib/auth';
import type { MenuProps } from 'antd';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const roleTagColors: Record<string, { bg: string; text: string; border: string }> = {
  ADMIN: { bg: '#EDE9FE', text: '#5B21B6', border: '#DDD6FE' },
  OWNER: { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A' },
  PURCHASER: { bg: '#DBEAFE', text: '#1E40AF', border: '#BFDBFE' },
  WAREHOUSE: { bg: '#D1FAE5', text: '#065F46', border: '#A7F3D0' },
  FINANCE_ADMIN: { bg: '#E0F2FE', text: '#0369A1', border: '#BAE6FD' },
  FINANCE_MANAGER: { bg: '#F3E8FF', text: '#7E22CE', border: '#E9D5FF' },
  MULTI_ROLE: { bg: '#FCE7F3', text: '#9D174D', border: '#FBCFE8' },
};

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const pathname = usePathname();
  const router = useRouter();
  const { user, activeRole, switchRole } = useAuth();

  // Detect mobile breakpoint
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Close drawer on route change
  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [pathname]);

  const authorizedNavItems = getAuthorizedNavItems(NAVIGATION_ITEMS, user.roles);

  const mapNavToMenuItems = (items: NavItemConfig[]): MenuProps['items'] => {
    return items.map((item) => {
      const isSubmenu = !!item.children && item.children.length > 0;
      return {
        key: isSubmenu ? item.key : item.path,
        icon: item.icon,
        label: isSubmenu ? (
          item.label
        ) : (
          <Link
            href={item.path}
            style={{
              color: 'inherit',
              display: 'inline-block',
              width: '100%',
              textDecoration: 'none',
            }}
          >
            {item.label}
          </Link>
        ),
        children: isSubmenu ? mapNavToMenuItems(item.children!) : undefined,
      };
    });
  };

  const menuItems = mapNavToMenuItems(authorizedNavItems);

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    router.push(e.key);
  };

  const getSelectedKey = () => {
    if (pathname === '/inventory/transactions') return '/inventory/transactions';
    if (pathname === '/inventory/catalog-summary') return '/inventory/catalog-summary';
    if (pathname.startsWith('/inventory')) return '/inventory';
    if (pathname.startsWith('/purchase-requests/approvals')) return '/purchase-requests/approvals';
    if (pathname.startsWith('/purchase-requests/new')) return '/purchase-requests/new';
    if (pathname.startsWith('/purchase-requests')) return '/purchase-requests';
    if (pathname.startsWith('/purchase-orders/consolidate')) return '/purchase-orders/consolidate';
    if (pathname.startsWith('/purchase-orders')) return '/purchase-orders';
    if (pathname.startsWith('/finance')) return '/finance';
    if (pathname.startsWith('/reports')) return '/reports';
    if (pathname.startsWith('/master')) return pathname;
    if (pathname.startsWith('/admin')) return '/admin';
    return pathname;
  };

  const getOpenKeys = () => {
    if (pathname.startsWith('/purchase-requests')) return ['/purchase-requests'];
    if (pathname.startsWith('/purchase-orders')) return ['/purchase-orders'];
    if (pathname.startsWith('/inventory')) return ['/inventory'];
    if (pathname.startsWith('/master')) return ['/master'];
    if (pathname.startsWith('/admin')) return ['/admin'];
    return [];
  };

  const SidebarLogo = () => (
    <div
      style={{
        height: 68,
        margin: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
        gap: 12,
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        paddingBottom: 12,
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          background: 'linear-gradient(135deg, #6366F1 0%, #818CF8 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontWeight: 700,
          fontSize: 18,
          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.35)',
          flexShrink: 0,
        }}
      >
        XYZ
      </div>
      {(!collapsed || isMobile) && (
        <div style={{ overflow: 'hidden' }}>
          <div
            style={{
              color: '#FFFFFF',
              fontWeight: 700,
              fontSize: 15,
              whiteSpace: 'nowrap',
              letterSpacing: '-0.01em',
            }}
          >
            PT XYZ ERP
          </div>
          <div
            style={{
              color: '#94A3B8',
              fontSize: 12,
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            Purchase & Procurement
          </div>
        </div>
      )}
      {isMobile && (
        <Button
          type="text"
          icon={<CloseOutlined style={{ color: '#94A3B8' }} />}
          onClick={() => setMobileDrawerOpen(false)}
          style={{ marginLeft: 'auto', padding: 4 }}
        />
      )}
    </div>
  );

  const SidebarMenu = () => (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[getSelectedKey()]}
      defaultOpenKeys={[
        ...getOpenKeys(),
        'purchase-requests',
        'purchase-orders',
        'inventory',
        'master-data',
        'admin',
        '/purchase-requests',
        '/purchase-orders',
        '/inventory',
        '/master',
        '/admin',
      ]}
      items={menuItems}
      onClick={handleMenuClick}
      style={{
        background: 'transparent',
        borderRight: 0,
      }}
    />
  );

  const UserInfoBlock = ({ compact = false }: { compact?: boolean }) => {
    return (
      <Space size={compact ? 6 : 'middle'}>
        {/* Role Simulator - DESKTOP ONLY */}
        {!compact && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              background: '#F8FAFC',
              padding: '4px 12px',
              borderRadius: 20,
              border: '1px solid #E2E8F0',
            }}
          >
            <SwapOutlined style={{ color: '#6366F1', fontSize: 13 }} />
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap' }}>
              Mode Simulasi:
            </Text>
            <Select
              value={activeRole}
              onChange={(val) => switchRole(val as any)}
              style={{ width: 170 }}
              variant="borderless"
              size="small"
              popupMatchSelectWidth={false}
              options={[
                { label: '👑 Admin', value: 'ADMIN' },
                { label: '✨ Owner', value: 'OWNER' },
                { label: '🛒 Purchaser', value: 'PURCHASER' },
                { label: '📦 Warehouse', value: 'WAREHOUSE' },
                { label: '📑 Finance Admin 1 (Tagihan)', value: 'FINANCE_ADMIN' },
                { label: '💵 Finance Admin 2 (Kasir)', value: 'FINANCE_CASHIER' },
                { label: '💼 Finance Manager', value: 'FINANCE_MANAGER' },
                { label: '⚡ Multi-Role', value: 'MULTI_ROLE' },
              ]}
            />
          </div>
        )}

        <Space size="small">
          <Avatar
            style={{
              background: 'linear-gradient(135deg, #6366F1 0%, #A855F7 100%)',
              boxShadow: '0 2px 8px rgba(99, 102, 241, 0.25)',
              flexShrink: 0,
              cursor: compact ? 'pointer' : 'default',
            }}
            size={compact ? 30 : 32}
            icon={<UserOutlined />}
            onClick={compact ? () => setMobileDrawerOpen(true) : undefined}
          />
          {!compact && (
            <div style={{ lineHeight: 1.3 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#0F172A' }}>{user.name}</div>
              <div style={{ marginTop: 2, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {user.roles.map((r) => {
                  const tagStyle = roleTagColors[r] || { bg: '#F1F5F9', text: '#475569', border: '#CBD5E1' };
                  return (
                    <span
                      key={r}
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '1px 6px',
                        borderRadius: 4,
                        background: tagStyle.bg,
                        color: tagStyle.text,
                        border: `1px solid ${tagStyle.border}`,
                        letterSpacing: '0.04em',
                      }}
                    >
                      {r}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </Space>
      </Space>
    );
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#F8FAFC', minWidth: 0, maxWidth: '100vw', overflowX: 'hidden' }}>
      {/* ── DESKTOP SIDEBAR (hidden on mobile via CSS desktop-sider) ── */}
      <Sider
        className="desktop-sider"
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        width={260}
        trigger={null}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
          background: '#0F172A',
          boxShadow: '2px 0 10px rgba(0, 0, 0, 0.05)',
        }}
      >
        <SidebarLogo />
        <SidebarMenu />
      </Sider>

      {/* ── MOBILE DRAWER SIDEBAR ── */}
      <Drawer
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        placement="left"
        size={290}
        closable={false}
        styles={{
          body: { padding: 0, background: '#0F172A' },
          wrapper: { background: '#0F172A' },
        }}
        style={{ background: '#0F172A' }}
      >
        <SidebarLogo />
        {/* User profile card inside mobile drawer */}
        <div
          style={{
            padding: '12px 16px',
            margin: '0 12px 12px 12px',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: 12,
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar
              size={36}
              style={{ background: 'linear-gradient(135deg, #6366F1 0%, #A855F7 100%)' }}
              icon={<UserOutlined />}
            />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ color: '#F8FAFC', fontWeight: 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.name}
              </div>
              <div style={{ color: '#94A3B8', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.email}
              </div>
            </div>
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {user.roles.map((r) => {
              const tagStyle = roleTagColors[r] || { bg: '#F1F5F9', text: '#475569', border: '#CBD5E1' };
              return (
                <span
                  key={r}
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '1px 6px',
                    borderRadius: 4,
                    background: tagStyle.bg,
                    color: tagStyle.text,
                    border: `1px solid ${tagStyle.border}`,
                  }}
                >
                  {r}
                </span>
              );
            })}
          </div>
        </div>

        {/* Role Simulator inside Drawer */}
        <div
          style={{
            margin: '0 12px 14px 12px',
            padding: '12px 14px',
            background: 'rgba(99, 102, 241, 0.12)',
            borderRadius: 12,
            border: '1px solid rgba(99, 102, 241, 0.25)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <SwapOutlined style={{ color: '#A5B4FC', fontSize: 13 }} />
            <span style={{ color: '#E0E7FF', fontSize: 12, fontWeight: 700 }}>
              Mode Simulasi Role
            </span>
          </div>
          <Select
            value={activeRole}
            onChange={(val) => switchRole(val as any)}
            style={{ width: '100%' }}
            size="middle"
            options={[
              { label: '👑 Admin (Semua Akses)', value: 'ADMIN' },
              { label: '✨ Owner (Evaluasi & Approval)', value: 'OWNER' },
              { label: '🛒 Purchaser (PO & Pengadaan)', value: 'PURCHASER' },
              { label: '📦 Warehouse (Penerimaan Gudang)', value: 'WAREHOUSE' },
              { label: '📑 Finance Admin 1 (Tagihan & Termin)', value: 'FINANCE_ADMIN' },
              { label: '💵 Finance Admin 2 (Kasir & Transfer)', value: 'FINANCE_CASHIER' },
              { label: '💼 Finance Manager (Otoritas Kas)', value: 'FINANCE_MANAGER' },
              { label: '⚡ Multi-Role (Purchaser + Gudang)', value: 'MULTI_ROLE' },
            ]}
          />
        </div>
        <SidebarMenu />
      </Drawer>

      {/* ── MAIN CONTENT AREA ── */}
      <Layout
        className="main-layout-wrapper"
        style={{
          marginLeft: isMobile ? 0 : (collapsed ? 80 : 260),
          width: isMobile ? '100%' : `calc(100% - ${collapsed ? 80 : 260}px)`,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          background: '#F8FAFC',
          minWidth: 0,
          maxWidth: isMobile ? '100%' : `calc(100% - ${collapsed ? 80 : 260}px)`,
          overflowX: 'hidden',
        }}
      >
        {/* Header */}
        <Header
          className="app-fixed-header"
          style={{
            padding: isMobile ? '0 12px' : '0 24px',
            background: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #F1F5F9',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)',
            position: 'fixed',
            top: 0,
            left: isMobile ? 0 : (collapsed ? 80 : 260),
            right: 0,
            zIndex: 999,
            height: isMobile ? 56 : 64,
            gap: 8,
            maxWidth: '100%',
            boxSizing: 'border-box',
            transition: 'left 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {/* Left: Hamburger + Title */}
          <Space size="small" style={{ minWidth: 0, flex: 1 }}>
            <span
              onClick={() => isMobile ? setMobileDrawerOpen(true) : setCollapsed(!collapsed)}
              style={{
                cursor: 'pointer',
                fontSize: 18,
                color: '#64748B',
                padding: '6px',
                borderRadius: 8,
                display: 'inline-flex',
                alignItems: 'center',
                transition: 'background 0.2s',
                flexShrink: 0,
              }}
            >
              {isMobile ? (
                <MenuOutlined />
              ) : collapsed ? (
                <MenuUnfoldOutlined />
              ) : (
                <MenuFoldOutlined />
              )}
            </span>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: '#6366F1',
                  boxShadow: '0 0 0 2px rgba(99, 102, 241, 0.2)',
                  flexShrink: 0,
                }}
              />
              <Text
                strong
                style={{
                  fontSize: isMobile ? 13 : 15,
                  color: '#1E293B',
                  letterSpacing: '-0.01em',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {isMobile ? 'XYZ ERP' : 'Purchase Management System'}
              </Text>
            </div>
          </Space>

          {/* Right: Role switcher + User */}
          <div style={{ flexShrink: 0 }}>
            <UserInfoBlock compact={isMobile} />
          </div>
        </Header>

        {/* Persistent Header Spacer so content is never covered by fixed header */}
        <div className="app-header-spacer" style={{ height: isMobile ? 56 : 64, flexShrink: 0 }} />

        {/* Page Content */}
        <Content
          className="site-content"
          style={{
            margin: isMobile ? '12px 10px 84px 10px' : '20px 24px',
            minHeight: 280,
            minWidth: 0,
            maxWidth: isMobile ? '100%' : 'calc(100% - 48px)',
            boxSizing: 'border-box',
          }}
        >
          {children}
        </Content>
      </Layout>

      {/* ── MOBILE BOTTOM NAVIGATION BAR ── */}
      {isMobile && (
        <div
          className="mobile-bottom-nav"
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            height: 58,
            background: '#FFFFFF',
            borderTop: '1px solid #E2E8F0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            zIndex: 1000,
            boxShadow: '0 -3px 12px rgba(0, 0, 0, 0.05)',
            boxSizing: 'border-box',
          }}
        >
          {/* 1. Beranda */}
          <Link
            href="/"
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              textDecoration: 'none',
              color: pathname === '/' ? '#4F46E5' : '#64748B',
              fontWeight: pathname === '/' ? 700 : 500,
              fontSize: 10.5,
              padding: '4px 0',
            }}
          >
            <div
              style={{
                width: 32,
                height: 22,
                borderRadius: 11,
                background: pathname === '/' ? '#EEF2FF' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 15,
                color: pathname === '/' ? '#4F46E5' : '#64748B',
              }}
            >
              <HomeOutlined />
            </div>
            <span>Beranda</span>
          </Link>

          {/* 2. PR */}
          <Link
            href="/purchase-requests"
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              textDecoration: 'none',
              color: pathname.startsWith('/purchase-requests') ? '#4F46E5' : '#64748B',
              fontWeight: pathname.startsWith('/purchase-requests') ? 700 : 500,
              fontSize: 10.5,
              padding: '4px 0',
            }}
          >
            <div
              style={{
                width: 32,
                height: 22,
                borderRadius: 11,
                background: pathname.startsWith('/purchase-requests') ? '#EEF2FF' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 15,
                color: pathname.startsWith('/purchase-requests') ? '#4F46E5' : '#64748B',
              }}
            >
              <FileTextOutlined />
            </div>
            <span>PR</span>
          </Link>

          {/* 3. PO */}
          <Link
            href="/purchase-orders"
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              textDecoration: 'none',
              color: pathname.startsWith('/purchase-orders') ? '#4F46E5' : '#64748B',
              fontWeight: pathname.startsWith('/purchase-orders') ? 700 : 500,
              fontSize: 10.5,
              padding: '4px 0',
            }}
          >
            <div
              style={{
                width: 32,
                height: 22,
                borderRadius: 11,
                background: pathname.startsWith('/purchase-orders') ? '#EEF2FF' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 15,
                color: pathname.startsWith('/purchase-orders') ? '#4F46E5' : '#64748B',
              }}
            >
              <ShoppingOutlined />
            </div>
            <span>PO</span>
          </Link>

          {/* 4. Gudang */}
          <Link
            href="/inventory"
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              textDecoration: 'none',
              color: pathname.startsWith('/inventory') || pathname.startsWith('/goods-receipts') ? '#4F46E5' : '#64748B',
              fontWeight: pathname.startsWith('/inventory') || pathname.startsWith('/goods-receipts') ? 700 : 500,
              fontSize: 10.5,
              padding: '4px 0',
            }}
          >
            <div
              style={{
                width: 32,
                height: 22,
                borderRadius: 11,
                background: pathname.startsWith('/inventory') || pathname.startsWith('/goods-receipts') ? '#EEF2FF' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 15,
                color: pathname.startsWith('/inventory') || pathname.startsWith('/goods-receipts') ? '#4F46E5' : '#64748B',
              }}
            >
              <InboxOutlined />
            </div>
            <span>Gudang</span>
          </Link>

          {/* 5. Menu Drawer */}
          <div
            onClick={() => setMobileDrawerOpen(true)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              cursor: 'pointer',
              color: mobileDrawerOpen ? '#4F46E5' : '#64748B',
              fontWeight: mobileDrawerOpen ? 700 : 500,
              fontSize: 10.5,
              padding: '4px 0',
            }}
          >
            <div
              style={{
                width: 32,
                height: 22,
                borderRadius: 11,
                background: mobileDrawerOpen ? '#EEF2FF' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 15,
                color: mobileDrawerOpen ? '#4F46E5' : '#64748B',
              }}
            >
              <AppstoreOutlined />
            </div>
            <span>Menu</span>
          </div>
        </div>
      )}

      <ToastContainer
        position={isMobile ? 'bottom-center' : 'top-right'}
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        style={isMobile ? { bottom: 'env(safe-area-inset-bottom, 0)', zIndex: 9999 } : undefined}
      />
    </Layout>
  );
}
